import React from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../styles/theme';

const BackButton = ({ 
  to, 
  label = 'Back',
  variant = 'default',
  size = 'md',
  showIcon = true,
  showLabel = true,
  onClick,
  style = {},
  ...props 
}) => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    } else if (to) {
      navigate(to);
    } else {
      navigate(-1);
    }
  };

  const sizeStyles = {
    sm: {
      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
      fontSize: theme.typography.fontSize.sm,
      iconSize: '16px',
      gap: theme.spacing.xs,
    },
    md: {
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      fontSize: theme.typography.fontSize.base,
      iconSize: '20px',
      gap: theme.spacing.sm,
    },
    lg: {
      padding: `${theme.spacing.md} ${theme.spacing.lg}`,
      fontSize: theme.typography.fontSize.lg,
      iconSize: '24px',
      gap: theme.spacing.sm,
    },
  };

  const variantStyles = {
    default: {
      backgroundColor: theme.colors.surface,
      color: theme.colors.text,
      border: `1px solid ${theme.colors.border}`,
      hoverBg: theme.colors.surfaceAlt,
      hoverBorder: theme.colors.primary,
      hoverColor: theme.colors.primary,
    },
    ghost: {
      backgroundColor: 'transparent',
      color: theme.colors.textSecondary,
      border: 'none',
      hoverBg: theme.colors.surfaceAlt,
      hoverBorder: 'none',
      hoverColor: theme.colors.primary,
    },
    solid: {
      backgroundColor: theme.colors.primary,
      color: theme.colors.textOnDark,
      border: 'none',
      hoverBg: theme.colors.primaryDark,
      hoverBorder: 'none',
      hoverColor: theme.colors.textOnDark,
    },
  };

  const currentSize = sizeStyles[size];
  const currentVariant = variantStyles[variant];

  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: currentSize.gap,
    padding: currentSize.padding,
    fontSize: currentSize.fontSize,
    fontFamily: theme.typography.fontFamily.secondary,
    fontWeight: theme.typography.fontWeight.medium,
    backgroundColor: currentVariant.backgroundColor,
    color: currentVariant.color,
    border: currentVariant.border,
    borderRadius: theme.borderRadius.md,
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 200ms ease-in-out',
    textDecoration: 'none',
    boxShadow: variant === 'default' ? theme.shadows.sm : 'none',
    ...style,
  };

  const [isHovered, setIsHovered] = React.useState(false);

  const hoverStyles = isHovered ? {
    backgroundColor: currentVariant.hoverBg,
    color: currentVariant.hoverColor,
    borderColor: currentVariant.hoverBorder || currentVariant.border,
    transform: 'translateX(-4px)',
    boxShadow: variant === 'default' ? theme.shadows.md : 'none',
  } : {};

  // SVG Arrow Icon
  const ArrowIcon = () => (
    <svg 
      width={currentSize.iconSize} 
      height={currentSize.iconSize} 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      style={{
        transition: 'transform 200ms ease-in-out',
        transform: isHovered ? 'translateX(-2px)' : 'translateX(0)',
      }}
    >
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
  );

  return (
    <button
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ ...baseStyles, ...hoverStyles }}
      {...props}
    >
      {showIcon && <ArrowIcon />}
      {showLabel && <span>{label}</span>}
    </button>
  );
};

export default BackButton;

